import React from 'react';
import { Send, ArrowUpCircle, ArrowDownCircle, CreditCard } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const Overview = ({ profile, transactions, notifications, setActiveTab }) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Account Balances</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Current Balance */}
              <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
                <p className="text-sm font-medium text-gray-600 mb-2">Current Balance</p>
                <div className="text-3xl font-bold text-blue-700">
                  ${profile.balance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
                <p className="text-xs text-gray-600 mt-2">Total account balance</p>
              </div>
              
              {/* Available Balance */}
              <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
                <p className="text-sm font-medium text-gray-600 mb-2">Available Balance</p>
                <div className="text-3xl font-bold text-green-700">
                  ${profile.balance.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                </div>
                <p className="text-xs text-gray-600 mt-2">Ready for withdrawal</p>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">
                Account Number: <span className="font-mono font-medium">{profile.accountNumber}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Account Status: <span className={`font-medium ${profile.status === 'active' ? 'text-green-600' : 'text-orange-600'}`}>
                  {profile.status.toUpperCase()}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              onClick={() => setActiveTab('transfer')} 
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" />
              Wire Transfer
            </Button>
            <Button 
              onClick={() => setActiveTab('transactions')} 
              variant="outline"
              className="w-full"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              View Transactions
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transactions.slice(0, 5).map((txn) => (
              <div key={txn.id} className="flex items-center justify-between border-b pb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    txn.type === 'credit' || txn.type === 'transfer_in' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {txn.type === 'credit' || txn.type === 'transfer_in' ? (
                      <ArrowUpCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <ArrowDownCircle className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{txn.description}</p>
                    <p className="text-sm text-gray-600">{txn.reference}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold ${
                    txn.type === 'credit' || txn.type === 'transfer_in' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {txn.type === 'credit' || txn.type === 'transfer_in' ? '+' : '-'}
                    ${txn.amount.toLocaleString('en-US', {minimumFractionDigits: 2})}
                  </p>
                  <p className="text-xs text-gray-600">{new Date(txn.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {notifications.length > 0 && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {notifications.slice(0, 5).map((notif) => (
                <div key={notif.id} className={`p-3 rounded-lg ${notif.read ? 'bg-gray-50' : 'bg-blue-50'}`}>
                  <p className="font-medium text-gray-900">{notif.title}</p>
                  <p className="text-sm text-gray-600">{notif.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{new Date(notif.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default Overview;
